import { useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import type { FormEvent, ReactNode } from 'react'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'
import { usePresence } from '../motion'

export interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  icon?: IconName
  /** A danger modal ignores a backdrop click: destructive acts need a decision. */
  tone?: 'default' | 'danger'
  footer?: ReactNode
  children: ReactNode
  width?: number
  /**
   * Makes the body and the footer one form: Enter in a one line field submits
   * it, and a footer button with `type="submit"` sends it. Without it the
   * modal is a plain panel.
   */
  onSubmit?: () => void
  /**
   * A request the modal started is in flight. Escape and the backdrop leave
   * it open, so the answer always lands on the screen that asked.
   */
  busy?: boolean
}

/** Where the focus goes when the modal opens: its first field, else its first button. */
const FIRST_FIELD = 'input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled])'

/** A radio group is entered on its chosen radio, as the keyboard expects. */
function entryOf(field: HTMLElement | null | undefined): HTMLElement | null | undefined {
  if (!(field instanceof HTMLInputElement) || field.type !== 'radio' || !field.name) return field
  const chosen = field.form
    ? field.form.querySelector<HTMLInputElement>(`input[type="radio"][name="${CSS.escape(field.name)}"]:checked`)
    : null
  return chosen ?? field
}

export function Modal({
  open,
  onClose,
  title,
  icon,
  tone = 'default',
  footer,
  children,
  width,
  onSubmit,
  busy,
}: ModalProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { mounted, closing } = usePresence(open)
  /** Whether the screen still wants the dialog, read after it has closed. */
  const live = useRef(open)
  // Screens pass a new `onClose` on every render. Read through a ref, it
  // never re-runs the effect below, which would pull the focus back to the
  // first field whenever the screen re-renders (a mutation starting, say).
  const close = useRef(onClose)
  const holding = useRef(busy)
  /** What had the focus before the dialog took it, to hand it back on close. */
  const opener = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    close.current = onClose
    holding.current = busy
    live.current = open
  })

  /**
   * What the dialog drew while open. A closing dialog fades out with these,
   * not with what the screen renders once it has cleared the state the
   * dialog was built from, which is often an empty title and no body.
   */
  const drawn = useRef({ title, icon, tone, footer, children, width, onSubmit })
  if (open) drawn.current = { title, icon, tone, footer, children, width, onSubmit }

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !holding.current) close.current()
      if (e.key !== 'Tab') return
      // Only what Tab can reach: an upload's file input sits out of the order.
      const focusable = [
        ...(ref.current?.querySelectorAll<HTMLElement>(
          'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ) ?? []),
      ].filter((el) => el.tabIndex >= 0)
      if (!focusable.length) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    // On the effect's second run the focus is already the dialog's own field.
    const active = document.activeElement as HTMLElement | null
    if (!ref.current?.contains(active)) opener.current = active
    // The first field, so a dialog that asks for a sentence opens ready for
    // it; with no field, the first button, which is the way out.
    const body = ref.current?.querySelector<HTMLElement>('.modal-b')
    const target =
      entryOf(body?.querySelector<HTMLElement>(FIRST_FIELD)) ??
      ref.current?.querySelector<HTMLElement>('button,a[href]')
    target?.focus()
    document.body.style.overflow = 'hidden'
    const dialog = ref.current
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
      // The focus goes back once the dialog is closed. A dialog the screen
      // still wants is only running this effect again (React's development
      // double run), and moving the focus out of it would mark its field as
      // left; a dialog opened in its place already holds the focus. A closing
      // dialog is still on screen for its exit, and gives the focus back now.
      requestAnimationFrame(() => {
        if (live.current) return
        const active = document.activeElement
        if (active && active !== document.body && !dialog?.contains(active)) return
        opener.current?.focus?.()
      })
    }
  }, [open])

  if (!mounted) return null
  const d = open ? { title, icon, tone, footer, children, width, onSubmit } : drawn.current

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!holding.current && live.current) d.onSubmit?.()
  }

  const content = (
    <>
      <div className="modal-b">{d.children}</div>
      {d.footer && <div className="modal-f">{d.footer}</div>}
    </>
  )

  return createPortal(
    <div className={cx('modal-root', closing && 'is-closing')} role="presentation" inert={closing || undefined}>
      <div
        className="scrim"
        onClick={d.tone === 'danger' || busy || closing ? undefined : onClose}
      />
      <div
        ref={ref}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={d.title}
        aria-busy={busy || undefined}
        style={d.width ? { inlineSize: d.width } : undefined}
      >
        <div className={cx('modal-h', 'row')} style={{ gap: 10 }}>
          {d.icon && (
            <span
              className="glyph"
              style={{
                inlineSize: 34,
                blockSize: 34,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 'var(--r-md)',
                background: d.tone === 'danger' ? 'var(--c-danger-soft)' : 'var(--c-accent-soft)',
                color: d.tone === 'danger' ? 'var(--c-danger)' : 'var(--c-accent)',
              }}
            >
              <Icon name={d.icon} />
            </span>
          )}
          <div className="t-h3">{d.title}</div>
        </div>
        {d.onSubmit ? (
          <form className="modal-form" onSubmit={submit} noValidate>
            {content}
          </form>
        ) : (
          content
        )}
      </div>
    </div>,
    document.body,
  )
}

/** The phone presentation of a modal: a bottom sheet, as 19-mobile draws it. */
export function Sheet({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  const { mounted, closing } = usePresence(open)
  const drawn = useRef(children)
  if (open) drawn.current = children
  if (!mounted) return null
  return createPortal(
    <div className={cx('sheet-root', closing && 'is-closing')} role="presentation" inert={closing || undefined}>
      <div className="scrim" onClick={closing ? undefined : onClose} />
      <div className="sheet-panel" role="dialog" aria-modal="true">
        <div className="sheet-grab" />
        {open ? children : drawn.current}
      </div>
    </div>,
    document.body,
  )
}
